export interface PlanetInterpretation {
  title: string
  text: string
  strengths: string[]
  challenges: string[]
}

// Helper to generate generic text for outer planets to save space while keeping the structure complete
const generateOuterPlanet = (
  planetName: string,
  signName: string,
  theme: string,
  strengths: string[],
  challenges: string[],
): PlanetInterpretation => ({
  title: `${planetName} в ${signName}`,
  text: `Положение ${planetName} в знаке ${signName} определяет ваше отношение к ${theme}. Это поколенческий аспект, который показывает, как вы взаимодействуете с глобальными процессами и трансформируете реальность вокруг себя. Вы стремитесь к развитию через призму качеств этого знака.`,
  strengths,
  challenges,
})

export const ASCENDANT_INTERPRETATIONS: Record<number, PlanetInterpretation> = {
  0: {
    title: 'Асцендент в Овне',
    text: 'Вы производите впечатление энергичного, решительного и прямолинейного человека. Окружающие видят в вас лидера, готового брать инициативу в свои руки. Ваша реакция на новые ситуации всегда быстрая и импульсивная.',
    strengths: ['Смелость', 'Инициативность', 'Открытость'],
    challenges: ['Нетерпеливость', 'Резкость', 'Склонность к спорам'],
  },
  1: {
    title: 'Асцендент в Тельце',
    text: 'Окружающие воспринимают вас как спокойного, надежного и основательного человека. Вы излучаете стабильность и миролюбие. В новых ситуациях вы предпочитаете действовать не спеша, оценивая практическую пользу.',
    strengths: ['Надежность', 'Терпение', 'Эстетический вкус'],
    challenges: ['Упрямство', 'Медлительность', 'Нежелание перемен'],
  },
  2: {
    title: 'Асцендент в Близнецах',
    text: 'Вы кажетесь людям общительным, подвижным и интеллектуальным человеком. Ваша мимика живая, а речь быстрая. Вы легко вступаете в контакт и быстро адаптируетесь к любой новой обстановке.',
    strengths: ['Коммуникабельность', 'Любознательность', 'Гибкость'],
    challenges: ['Поверхностность', 'Нервозность', 'Непостоянство'],
  },
  3: {
    title: 'Асцендент в Раке',
    text: 'Вы производите впечатление мягкого, заботливого и чувствительного человека. В незнакомой обстановке вы можете казаться застенчивым и закрытым, пока не почувствуете себя в безопасности.',
    strengths: ['Эмпатия', 'Заботливость', 'Интуиция'],
    challenges: ['Обидчивость', 'Закрытость', 'Переменчивость настроения'],
  },
  4: {
    title: 'Асцендент во Льве',
    text: 'Окружающие видят в вас яркую, уверенную в себе и харизматичную личность. Вы умеете подать себя и часто оказываетесь в центре внимания. Ваша манера держаться полна достоинства и благородства.',
    strengths: ['Харизма', 'Щедрость', 'Уверенность'],
    challenges: ['Гордыня', 'Эгоцентризм', 'Потребность во внимании'],
  },
  5: {
    title: 'Асцендент в Деве',
    text: 'Вы кажетесь людям скромным, аккуратным и аналитичным человеком. Вы обращаете внимание на детали и стремитесь к порядку во всем. В новых ситуациях вы ведете себя сдержанно и практично.',
    strengths: ['Внимательность', 'Практичность', 'Трудолюбие'],
    challenges: ['Критичность', 'Перфекционизм', 'Тревожность'],
  },
  6: {
    title: 'Асцендент в Весах',
    text: 'Окружающие воспринимают вас как обаятельного, дипломатичного и приятного в общении человека. Вы стремитесь к гармонии и избегаете конфликтов. Ваш стиль отличается хорошим вкусом и элегантностью.',
    strengths: ['Дипломатичность', 'Обаяние', 'Справедливость'],
    challenges: [
      'Нерешительность',
      'Зависимость от чужого мнения',
      'Избегание проблем',
    ],
  },
  7: {
    title: 'Асцендент в Скорпионе',
    text: 'Вы производите впечатление загадочного, магнетического и проницательного человека. Ваш взгляд часто кажется пронзительным. Вы не спешите раскрываться перед незнакомыми людьми, сохраняя дистанцию.',
    strengths: ['Проницательность', 'Магнетизм', 'Стойкость'],
    challenges: ['Скрытность', 'Подозрительность', 'Властность'],
  },
  8: {
    title: 'Асцендент в Стрельце',
    text: 'Вы кажетесь людям оптимистичным, открытым и жизнерадостным человеком. Ваша улыбка искренняя, а манеры свободные. Вы легко идете на контакт и заражаете окружающих своим энтузиазмом.',
    strengths: ['Оптимизм', 'Искренность', 'Широта взглядов'],
    challenges: [
      'Бестактность',
      'Необязательность',
      'Склонность к преувеличениям',
    ],
  },
  9: {
    title: 'Асцендент в Козероге',
    text: 'Окружающие видят в вас серьезного, ответственного и целеустремленного человека. Вы держитесь с достоинством и некоторой дистанцией. В новых ситуациях вы проявляете осторожность и прагматизм.',
    strengths: ['Ответственность', 'Целеустремленность', 'Надежность'],
    challenges: ['Холодность', 'Излишняя строгость', 'Пессимизм'],
  },
  10: {
    title: 'Асцендент в Водолее',
    text: 'Вы производите впечатление оригинального, независимого и дружелюбного человека. Вы выделяетесь из толпы своими взглядами или стилем. Вы легко общаетесь со всеми, но сохраняете внутреннюю свободу.',
    strengths: ['Оригинальность', 'Дружелюбие', 'Широта мышления'],
    challenges: ['Непредсказуемость', 'Отстраненность', 'Упрямство'],
  },
  11: {
    title: 'Асцендент в Рыбах',
    text: 'Вы кажетесь людям мягким, мечтательным и сострадательным человеком. Ваша энергетика неуловима и загадочна. Вы легко подстраиваетесь под окружающую обстановку и тонко чувствуете настроение других.',
    strengths: ['Сострадание', 'Интуиция', 'Творческий подход'],
    challenges: ['Рассеянность', 'Впечатлительность', 'Склонность к иллюзиям'],
  },
}

export const PLANET_INTERPRETATIONS: Record<
  string,
  Record<number, PlanetInterpretation>
> = {
  sun: {
    0: {
      title: 'Солнце в Овне',
      text: 'Ваше ядро личности — это огонь, действие и инициатива. Вы прирожденный лидер, который не боится трудностей и всегда готов прокладывать новые пути. Ваша энергия направлена вовне, вы стремитесь быть первым и побеждать.',
      strengths: ['Смелость', 'Решительность', 'Энтузиазм'],
      challenges: [
        'Импульсивность',
        'Агрессивность',
        'Неумение доводить дела до конца',
      ],
    },
    1: {
      title: 'Солнце в Тельце',
      text: 'Ваша суть — стабильность, созидание и наслаждение материальным миром. Вы цените комфорт, красоту и безопасность. Ваша сила в умении планомерно и упорно двигаться к своим целям, создавая прочный фундамент.',
      strengths: ['Упорство', 'Надежность', 'Практичность'],
      challenges: ['Упрямство', 'Лень', 'Собственнический инстинкт'],
    },
    2: {
      title: 'Солнце в Близнецах',
      text: 'Ваше ядро личности — это интеллект, коммуникация и постоянное движение. Вы любознательны, гибки и легко усваиваете новую информацию. Ваша задача — связывать людей и идеи, передавать знания.',
      strengths: ['Интеллект', 'Общительность', 'Адаптивность'],
      challenges: ['Поверхностность', 'Непостоянство', 'Нервозность'],
    },
    3: {
      title: 'Солнце в Раке',
      text: 'Ваша суть — эмоции, забота и сохранение традиций. Вы глубоко чувствующий человек, для которого семья и внутренний мир важнее внешних достижений. Ваша сила в эмпатии и умении создавать уют.',
      strengths: ['Эмпатия', 'Заботливость', 'Интуиция'],
      challenges: [
        'Обидчивость',
        'Зависимость от прошлого',
        'Переменчивость настроения',
      ],
    },
    4: {
      title: 'Солнце во Льве',
      text: 'Ваше ядро личности — это творчество, самовыражение и благородство. Вы рождены, чтобы сиять и вдохновлять других. Ваша энергия теплая и щедрая, вы стремитесь к признанию и любви окружающих.',
      strengths: ['Харизма', 'Щедрость', 'Творческий потенциал'],
      challenges: ['Эгоцентризм', 'Гордыня', 'Зависимость от лести'],
    },
    5: {
      title: 'Солнце в Деве',
      text: 'Ваша суть — служение, анализ и стремление к совершенству. Вы практичны, внимательны к деталям и умеете наводить порядок в хаосе. Ваша сила в трудолюбии и способности улучшать все, к чему вы прикасаетесь.',
      strengths: ['Аналитический ум', 'Трудолюбие', 'Надежность'],
      challenges: ['Критичность', 'Перфекционизм', 'Тревожность'],
    },
    6: {
      title: 'Солнце в Весах',
      text: 'Ваше ядро личности — это партнерство, гармония и эстетика. Вы стремитесь к балансу во всем и умеете находить компромиссы. Ваша задача — приносить в мир красоту и справедливость через взаимодействие с другими.',
      strengths: ['Дипломатичность', 'Обаяние', 'Чувство прекрасного'],
      challenges: [
        'Нерешительность',
        'Зависимость от чужого мнения',
        'Избегание конфликтов',
      ],
    },
    7: {
      title: 'Солнце в Скорпионе',
      text: 'Ваша суть — трансформация, интенсивность и глубокие эмоции. Вы обладаете огромной внутренней силой и проницательностью. Ваша задача — исследовать скрытые стороны жизни и возрождаться после кризисов.',
      strengths: ['Сила воли', 'Проницательность', 'Страстность'],
      challenges: [
        'Мстительность',
        'Подозрительность',
        'Склонность к саморазрушению',
      ],
    },
    8: {
      title: 'Солнце в Стрельце',
      text: 'Ваше ядро личности — это расширение горизонтов, оптимизм и поиск смысла. Вы философ и путешественник, стремящийся познать мир. Ваша энергия направлена на духовный рост и вдохновение других.',
      strengths: ['Оптимизм', 'Мудрость', 'Широта взглядов'],
      challenges: ['Бестактность', 'Догматизм', 'Необязательность'],
    },
    9: {
      title: 'Солнце в Козероге',
      text: 'Ваша суть — структура, ответственность и достижение целей. Вы амбициозны, дисциплинированны и готовы упорно трудиться ради результата. Ваша сила в умении планировать и преодолевать любые препятствия.',
      strengths: ['Целеустремленность', 'Ответственность', 'Практичность'],
      challenges: ['Пессимизм', 'Холодность', 'Трудоголизм'],
    },
    10: {
      title: 'Солнце в Водолее',
      text: 'Ваше ядро личности — это свобода, инновации и гуманизм. Вы оригинальны, независимы и устремлены в будущее. Ваша задача — разрушать устаревшие рамки и приносить в мир новые, прогрессивные идеи.',
      strengths: ['Оригинальность', 'Независимость', 'Гуманизм'],
      challenges: [
        'Непредсказуемость',
        'Упрямство',
        'Эмоциональная отстраненность',
      ],
    },
    11: {
      title: 'Солнце в Рыбах',
      text: 'Ваша суть — духовность, сострадание и интуиция. Вы тонко чувствующий человек, связанный с коллективным бессознательным. Ваша сила в творчестве, эмпатии и способности к безусловной любви.',
      strengths: ['Интуиция', 'Сострадание', 'Творческие способности'],
      challenges: ['Иллюзии', 'Жертвенность', 'Уход от реальности'],
    },
  },
  moon: {
    0: {
      title: 'Луна в Овне',
      text: 'Ваши эмоции вспыхивают быстро и ярко. Вы нуждаетесь в действии и независимости для эмоционального комфорта. Вы прямолинейны в выражении чувств и быстро забываете обиды.',
      strengths: [
        'Искренность',
        'Эмоциональная смелость',
        'Быстрая отходчивость',
      ],
      challenges: ['Вспыльчивость', 'Нетерпеливость', 'Эгоцентризм в чувствах'],
    },
    1: {
      title: 'Луна в Тельце',
      text: 'Ваш внутренний мир стабилен и спокоен. Для эмоционального равновесия вам необходимы материальный комфорт, вкусная еда и уверенность в завтрашнем дне. Вы верны в своих привязанностях.',
      strengths: [
        'Эмоциональная стабильность',
        'Надежность',
        'Умение наслаждаться жизнью',
      ],
      challenges: ['Упрямство', 'Собственничество', 'Страх перемен'],
    },
    2: {
      title: 'Луна в Близнецах',
      text: 'Вы перерабатываете эмоции через интеллект и общение. Для душевного комфорта вам нужны постоянная смена впечатлений, разговоры и новая информация. Ваши чувства могут быть переменчивыми.',
      strengths: ['Легкость', 'Адаптивность', 'Умение проговаривать чувства'],
      challenges: [
        'Поверхностность эмоций',
        'Нервозность',
        'Рационализация чувств',
      ],
    },
    3: {
      title: 'Луна в Раке',
      text: 'Это сильное положение Луны. Вы обладаете глубокой эмоциональностью и сильной интуицией. Для комфорта вам необходимы безопасный дом, семья и тесные эмоциональные связи.',
      strengths: ['Глубокая эмпатия', 'Заботливость', 'Сильная интуиция'],
      challenges: [
        'Сверхчувствительность',
        'Обидчивость',
        'Зависимость от прошлого',
      ],
    },
    4: {
      title: 'Луна во Льве',
      text: 'Ваши эмоции драматичны и благородны. Вы нуждаетесь во внимании, признании и восхищении для душевного комфорта. Вы щедры на чувства и любите радовать близких.',
      strengths: ['Щедрость души', 'Оптимизм', 'Творческое выражение чувств'],
      challenges: ['Драматизация', 'Зависимость от похвалы', 'Гордыня'],
    },
    5: {
      title: 'Луна в Деве',
      text: 'Вы выражаете заботу через практическую помощь и служение. Для эмоционального комфорта вам нужны порядок, чистота и понимание того, что вы приносите пользу. Вы склонны анализировать свои чувства.',
      strengths: [
        'Заботливость в делах',
        'Практичность',
        'Внимательность к нуждам других',
      ],
      challenges: [
        'Тревожность',
        'Критичность к себе и другим',
        'Неумение расслабляться',
      ],
    },
    6: {
      title: 'Луна в Весах',
      text: 'Для душевного равновесия вам необходимы гармония, красота и партнерство. Вы плохо переносите конфликты и грубость. Вы подсознательно стремитесь нравиться окружающим и находить компромиссы.',
      strengths: ['Миролюбие', 'Дипломатичность', 'Эстетическое чутье'],
      challenges: [
        'Зависимость от партнера',
        'Нерешительность',
        'Подавление негативных эмоций',
      ],
    },
    7: {
      title: 'Луна в Скорпионе',
      text: 'Ваши эмоции невероятно глубоки, интенсивны и часто скрыты от посторонних. Вы нуждаетесь в глубоких, трансформирующих переживаниях. Вы обладаете мощной интуицией и способностью к регенерации.',
      strengths: [
        'Психологическая проницательность',
        'Эмоциональная стойкость',
        'Преданность',
      ],
      challenges: ['Ревность', 'Мстительность', 'Склонность к саморазрушению'],
    },
    8: {
      title: 'Луна в Стрельце',
      text: 'Ваш внутренний мир полон оптимизма и веры в лучшее. Для комфорта вам нужны свобода, путешествия и философский смысл. Вы щедры на эмоции и легко вдохновляетесь новыми идеями.',
      strengths: ['Оптимизм', 'Искренность', 'Широта души'],
      challenges: [
        'Избегание сложных эмоций',
        'Беспокойство',
        'Необязательность',
      ],
    },
    9: {
      title: 'Луна в Козероге',
      text: 'Вы склонны сдерживать и контролировать свои эмоции. Для душевного комфорта вам нужны статус, достижения и четкая структура. Вы выражаете любовь через ответственность и обеспечение безопасности.',
      strengths: ['Эмоциональная выдержка', 'Надежность', 'Ответственность'],
      challenges: [
        'Холодность',
        'Подавление чувств',
        'Склонность к депрессиям',
      ],
    },
    10: {
      title: 'Луна в Водолее',
      text: 'Ваши эмоции свободны и независимы. Вы нуждаетесь в личном пространстве и интеллектуальном общении для душевного комфорта. Вы гуманны, но можете казаться эмоционально отстраненным.',
      strengths: ['Объективность', 'Дружелюбие', 'Свобода от предрассудков'],
      challenges: [
        'Эмоциональная холодность',
        'Непредсказуемость',
        'Страх близости',
      ],
    },
    11: {
      title: 'Луна в Рыбах',
      text: 'Вы обладаете безграничной эмпатией и чувствительностью. Вы впитываете эмоции окружающих как губка. Для комфорта вам нужны уединение, музыка, творчество или духовные практики.',
      strengths: ['Сострадание', 'Мощная интуиция', 'Творческое воображение'],
      challenges: [
        'Размытые личные границы',
        'Склонность к иллюзиям',
        'Эскапизм',
      ],
    },
  },
  mercury: {
    0: {
      title: 'Меркурий в Овне',
      text: 'Ваше мышление быстрое, решительное и независимое. Вы говорите прямо, иногда резко, и быстро схватываете суть. Вы отличный генератор идей, но можете терять интерес к деталям.',
      strengths: ['Остроумие', 'Прямолинейность', 'Быстрота реакции'],
      challenges: ['Нетерпеливость', 'Склонность к спорам', 'Поспешные выводы'],
    },
    1: {
      title: 'Меркурий в Тельце',
      text: 'Ваш ум работает основательно, практично и неторопливо. Вы долго обдумываете решения, но приняв их, редко меняете. Ваша речь размеренна, а идеи всегда имеют практическое применение.',
      strengths: ['Здравый смысл', 'Основательность', 'Хорошая память'],
      challenges: ['Упрямство', 'Медлительность мышления', 'Консерватизм'],
    },
    2: {
      title: 'Меркурий в Близнецах',
      text: 'Это сильное положение. Ваш ум невероятно подвижен, любознателен и многозадачен. Вы легко усваиваете информацию, прекрасно говорите и пишете, обожаете учиться и общаться.',
      strengths: ['Красноречие', 'Сообразительность', 'Эрудиция'],
      challenges: ['Поверхностность', 'Рассеянность', 'Болтливость'],
    },
    3: {
      title: 'Меркурий в Раке',
      text: 'Ваше мышление тесно связано с эмоциями и интуицией. Вы обладаете прекрасной памятью на чувства и события прошлого. В общении вы мягки, эмпатичны и умеете слушать.',
      strengths: [
        'Интуитивное понимание',
        'Хорошая память',
        'Поэтичность речи',
      ],
      challenges: ['Субъективность', 'Обидчивость в спорах', 'Нелогичность'],
    },
    4: {
      title: 'Меркурий во Льве',
      text: 'Ваш ум творческий, масштабный и уверенный. Вы говорите убедительно, ярко и с достоинством, умеете увлечь аудиторию. Ваши идеи часто амбициозны и направлены на самовыражение.',
      strengths: [
        'Ораторское искусство',
        'Убедительность',
        'Творческое мышление',
      ],
      challenges: [
        'Догматизм',
        'Неумение слушать других',
        'Интеллектуальная гордыня',
      ],
    },
    5: {
      title: 'Меркурий в Деве',
      text: 'Это сильное положение. Ваш ум аналитичен, точен и невероятно внимателен к деталям. Вы прекрасно систематизируете информацию, обладаете логикой и практической смекалкой.',
      strengths: ['Аналитические способности', 'Точность', 'Логика'],
      challenges: [
        'Занудство',
        'Излишняя критичность',
        'Зацикленность на мелочах',
      ],
    },
    6: {
      title: 'Меркурий в Весах',
      text: 'Ваше мышление направлено на поиск баланса, справедливости и компромисса. Вы общаетесь вежливо, дипломатично и красиво. Вы умеете видеть ситуацию с разных сторон.',
      strengths: ['Дипломатичность', 'Объективность', 'Тактичность'],
      challenges: [
        'Нерешительность',
        'Избегание острых углов',
        'Зависимость от мнения собеседника',
      ],
    },
    7: {
      title: 'Меркурий в Скорпионе',
      text: 'Ваш ум проницателен, глубок и въедлив. Вы видите скрытые мотивы людей и суть вещей. Ваша речь может быть язвительной, но всегда бьет точно в цель. Вы отличный исследователь.',
      strengths: [
        'Проницательность',
        'Анализ скрытого',
        'Интеллектуальная смелость',
      ],
      challenges: ['Сарказм', 'Подозрительность', 'Скрытность'],
    },
    8: {
      title: 'Меркурий в Стрельце',
      text: 'Ваше мышление масштабное, философское и оптимистичное. Вы мыслите концепциями, а не деталями. Вы любите учить, философствовать и обсуждать глобальные идеи.',
      strengths: ['Широта взглядов', 'Искренность', 'Способность вдохновлять'],
      challenges: [
        'Бестактность',
        'Невнимательность к деталям',
        'Склонность к преувеличениям',
      ],
    },
    9: {
      title: 'Меркурий в Козероге',
      text: 'Ваш ум структурирован, серьезен и прагматичен. Вы мыслите логично, цените факты и дисциплину ума. Ваша речь лаконична, по существу, без лишних эмоций.',
      strengths: ['Концентрация', 'Логика', 'Организаторские способности'],
      challenges: ['Пессимизм', 'Узость мышления', 'Излишняя серьезность'],
    },
    10: {
      title: 'Меркурий в Водолее',
      text: 'Ваше мышление оригинально, независимо и устремлено в будущее. Вы легко генерируете нестандартные идеи и понимаете сложные концепции. Вы общаетесь на равных со всеми.',
      strengths: ['Изобретательность', 'Объективность', 'Прогрессивность'],
      challenges: [
        'Упрямство в идеях',
        'Интеллектуальный снобизм',
        'Оторванность от реальности',
      ],
    },
    11: {
      title: 'Меркурий в Рыбах',
      text: 'Ваш ум интуитивен, образен и поэтичен. Вы мыслите не логикой, а образами и чувствами. Вы обладаете богатой фантазией и способны к глубокому творческому самовыражению.',
      strengths: ['Богатое воображение', 'Интуиция', 'Эмпатия в общении'],
      challenges: ['Рассеянность', 'Нелогичность', 'Склонность к самообману'],
    },
  },
  venus: {
    0: {
      title: 'Венера в Овне',
      text: 'В любви вы страстны, импульсивны и инициативны. Вы быстро влюбляетесь и любите завоевывать партнера. Ваши чувства горят ярко, но могут быстро остыть без динамики.',
      strengths: ['Страстность', 'Искренность чувств', 'Смелость в любви'],
      challenges: ['Эгоизм в отношениях', 'Непостоянство', 'Нетерпеливость'],
    },
    1: {
      title: 'Венера в Тельце',
      text: 'Это сильное положение. В любви вы цените стабильность, верность и чувственные удовольствия. Вы выражаете любовь через прикосновения, подарки и создание комфорта.',
      strengths: ['Верность', 'Чувственность', 'Умение создавать красоту'],
      challenges: ['Собственничество', 'Ревность', 'Лень'],
    },
    2: {
      title: 'Венера в Близнецах',
      text: 'В любви для вас главное — интеллектуальный контакт, общение и легкость. Вы ищете партнера-друга, с которым интересно разговаривать. Вы цените разнообразие и флирт.',
      strengths: [
        'Легкость в общении',
        'Обаяние',
        'Интеллектуальная привлекательность',
      ],
      challenges: [
        'Ветреность',
        'Поверхностность чувств',
        'Страх глубокой привязанности',
      ],
    },
    3: {
      title: 'Венера в Раке',
      text: 'В любви вы нежны, заботливы и очень привязчивы. Вы ищете эмоциональную безопасность и глубокое родство душ. Вы выражаете любовь через опеку и создание семейного уюта.',
      strengths: ['Преданность', 'Нежность', 'Глубокая эмпатия'],
      challenges: [
        'Обидчивость',
        'Собственничество',
        'Эмоциональная зависимость',
      ],
    },
    4: {
      title: 'Венера во Льве',
      text: 'В любви вы щедры, романтичны и любите красивые жесты. Вы ищете партнера, которым сможете гордиться. Вы выражаете чувства ярко, драматично и ждете восхищения в ответ.',
      strengths: ['Щедрость', 'Романтичность', 'Верность'],
      challenges: [
        'Гордыня',
        'Потребность во внимании',
        'Драматизация отношений',
      ],
    },
    5: {
      title: 'Венера в Деве',
      text: 'В любви вы практичны, разборчивы и сдержанны. Вы выражаете любовь через реальную помощь, заботу о здоровье и делах партнера. Вы стремитесь к идеальным отношениям.',
      strengths: ['Надежность', 'Заботливость', 'Верность'],
      challenges: ['Критичность к партнеру', 'Холодность', 'Перфекционизм'],
    },
    6: {
      title: 'Венера в Весах',
      text: 'Это сильное положение. Вы созданы для партнерства, романтики и красоты. Вы обаятельны, тактичны и умеете создавать гармонию в отношениях. Эстетика для вас очень важна.',
      strengths: ['Обаяние', 'Дипломатичность', 'Утонченный вкус'],
      challenges: [
        'Зависимость от партнера',
        'Поверхностность',
        'Избегание проблем',
      ],
    },
    7: {
      title: 'Венера в Скорпионе',
      text: 'В любви вы ищете глубину, интенсивность и полное слияние. Ваши чувства невероятно страстны и магнетичны. Вы требуете абсолютной верности и честности от партнера.',
      strengths: ['Глубокая преданность', 'Страстность', 'Магнетизм'],
      challenges: ['Ревность', 'Властность', 'Склонность к манипуляциям'],
    },
    8: {
      title: 'Венера в Стрельце',
      text: 'В любви вы цените свободу, совместные приключения и духовное родство. Вы идеалистичны, щедры и оптимистичны в отношениях. Вы ищете партнера, с которым можно развиваться.',
      strengths: ['Искренность', 'Оптимизм', 'Щедрость'],
      challenges: [
        'Непостоянство',
        'Страх обязательств',
        'Идеализация партнера',
      ],
    },
    9: {
      title: 'Венера в Козероге',
      text: 'В любви вы серьезны, ответственны и осторожны. Вы долго присматриваетесь, но выбрав партнера, сохраняете верность. Вы цените статус, надежность и практическую поддержку.',
      strengths: ['Верность', 'Надежность', 'Ответственность'],
      challenges: [
        'Эмоциональная холодность',
        'Расчетливость',
        'Излишняя строгость',
      ],
    },
    10: {
      title: 'Венера в Водолее',
      text: 'В любви вы цените дружбу, свободу и интеллектуальное равенство. Вы не терпите ограничений и ревности. Ваши отношения часто строятся на общих интересах и нестандартных взглядах.',
      strengths: ['Дружелюбие', 'Толерантность', 'Уважение к свободе партнера'],
      challenges: [
        'Отстраненность',
        'Непредсказуемость',
        'Страх эмоциональной близости',
      ],
    },
    11: {
      title: 'Венера в Рыбах',
      text: 'Это сильное положение. В любви вы способны на высшую степень сострадания, жертвенности и безусловного принятия. Вы невероятно романтичны, чувствительны и идеалистичны.',
      strengths: ['Безусловная любовь', 'Романтичность', 'Глубокая эмпатия'],
      challenges: [
        'Иллюзии',
        'Позиция жертвы',
        'Размытые границы в отношениях',
      ],
    },
  },
  mars: {
    0: {
      title: 'Марс в Овне',
      text: 'Это сильное положение. Ваша энергия бьет ключом, вы действуете быстро, решительно и смело. Вы прирожденный боец и первопроходец, любите соревноваться и побеждать.',
      strengths: ['Смелость', 'Инициативность', 'Неиссякаемая энергия'],
      challenges: ['Агрессивность', 'Импульсивность', 'Быстрое выгорание'],
    },
    1: {
      title: 'Марс в Тельце',
      text: 'Вы действуете медленно, но невероятно упорно и основательно. Вашу энергию трудно раскачать, но если вы начали дело, то доведете его до конца. Вы выносливы и практичны.',
      strengths: ['Упорство', 'Выносливость', 'Практичность действий'],
      challenges: ['Лень', 'Упрямство', 'Инертность'],
    },
    2: {
      title: 'Марс в Близнецах',
      text: 'Ваша энергия направлена на общение, обучение и перемещения. Вы действуете быстро, многозадачно, но можете распыляться. Ваше главное оружие — это слово и интеллект.',
      strengths: ['Многозадачность', 'Ловкость', 'Интеллектуальная активность'],
      challenges: ['Непостоянство', 'Рассеянность', 'Нервозность'],
    },
    3: {
      title: 'Марс в Раке',
      text: 'Вы действуете под влиянием эмоций и настроения. Ваша энергия направлена на защиту семьи и близких. Вы избегаете прямых конфликтов, предпочитая обходные пути.',
      strengths: [
        'Упорство в защите своего',
        'Интуитивные действия',
        'Выносливость',
      ],
      challenges: [
        'Обидчивость',
        'Пассивная агрессия',
        'Зависимость действий от настроения',
      ],
    },
    4: {
      title: 'Марс во Льве',
      text: 'Вы действуете уверенно, ярко и с достоинством. Ваша энергия направлена на творчество, лидерство и достижение признания. Вы благородны в борьбе и любите масштабные проекты.',
      strengths: ['Лидерство', 'Уверенность', 'Творческая энергия'],
      challenges: ['Гордыня', 'Эгоцентризм', 'Неумение подчиняться'],
    },
    5: {
      title: 'Марс в Деве',
      text: 'Ваша энергия направлена на работу, служение и наведение порядка. Вы действуете методично, точно и невероятно эффективно. Вы мастер своего дела, внимательный к деталям.',
      strengths: ['Трудолюбие', 'Точность', 'Эффективность'],
      challenges: ['Перфекционизм', 'Зацикленность на мелочах', 'Критичность'],
    },
    6: {
      title: 'Марс в Весах',
      text: 'Вы действуете дипломатично, стремясь к сотрудничеству и избегая открытых конфликтов. Ваша энергия направлена на создание гармонии и справедливости. Вы лучше работаете в партнерстве.',
      strengths: [
        'Дипломатичность',
        'Умение договариваться',
        'Стремление к справедливости',
      ],
      challenges: [
        'Нерешительность',
        'Избегание прямых действий',
        'Зависимость от партнера',
      ],
    },
    7: {
      title: 'Марс в Скорпионе',
      text: 'Это сильное положение. Ваша энергия скрыта, но невероятно мощна и интенсивна. Вы действуете стратегически, обладаете огромной выносливостью и способны выживать в кризисах.',
      strengths: ['Мощная воля', 'Стратегическое мышление', 'Выносливость'],
      challenges: [
        'Мстительность',
        'Скрытая агрессия',
        'Склонность к разрушению',
      ],
    },
    8: {
      title: 'Марс в Стрельце',
      text: 'Вы действуете с энтузиазмом, оптимизмом и размахом. Ваша энергия направлена на расширение горизонтов, путешествия и защиту идеалов. Вы вдохновляете других своими действиями.',
      strengths: ['Энтузиазм', 'Прямолинейность', 'Энергичность'],
      challenges: ['Необязательность', 'Распыление энергии', 'Бестактность'],
    },
    9: {
      title: 'Марс в Козероге',
      text: 'Это сильное положение. Вы действуете дисциплинированно, амбициозно и невероятно упорно. Ваша энергия направлена на достижение конкретных, долгосрочных целей и статуса.',
      strengths: [
        'Дисциплина',
        'Целеустремленность',
        'Организаторские способности',
      ],
      challenges: ['Жесткость', 'Трудоголизм', 'Холодность'],
    },
    10: {
      title: 'Марс в Водолее',
      text: 'Вы действуете оригинально, независимо и непредсказуемо. Ваша энергия направлена на реформы, командную работу и воплощение прогрессивных идей. Вы борец за свободу.',
      strengths: ['Новаторство', 'Командный дух', 'Независимость'],
      challenges: ['Упрямство', 'Бунтарство ради бунта', 'Непредсказуемость'],
    },
    11: {
      title: 'Марс в Рыбах',
      text: 'Ваша энергия мягкая, интуитивная и часто скрытая. Вы действуете по вдохновению, избегая жесткой борьбы. Ваша сила в творчестве, сострадании и умении действовать незаметно.',
      strengths: ['Интуитивные действия', 'Творческая энергия', 'Сострадание'],
      challenges: ['Нерешительность', 'Лень', 'Склонность плыть по течению'],
    },
  },
}

// Generate outer planets to save space but maintain structure
const SIGN_NAMES = [
  'Овен',
  'Телец',
  'Близнецы',
  'Рак',
  'Лев',
  'Дева',
  'Весы',
  'Скорпион',
  'Стрелец',
  'Козерог',
  'Водолей',
  'Рыбы',
]
const outerPlanets = ['jupiter', 'saturn', 'uranus', 'neptune', 'pluto']
const outerPlanetNames = ['Юпитер', 'Сатурн', 'Уран', 'Нептун', 'Плутон']
const outerThemes = [
  'расширению, удаче и мировоззрению',
  'дисциплине, ограничениям и карьерному росту',
  'свободе, инновациям и внезапным переменам',
  'духовности, иллюзиям и высшей любви',
  'трансформации, власти и глубоким кризисам',
]

outerPlanets.forEach((planet, idx) => {
  PLANET_INTERPRETATIONS[planet] = {}
  SIGN_NAMES.forEach((signName, signIdx) => {
    PLANET_INTERPRETATIONS[planet][signIdx] = generateOuterPlanet(
      outerPlanetNames[idx],
      signName,
      outerThemes[idx],
      ['Глобальное видение', 'Потенциал роста', 'Глубокая трансформация'],
      [
        'Сложность адаптации',
        'Внутренние противоречия',
        'Зависимость от эпохи',
      ],
    )
  })
})

export const ASPECT_INTERPRETATIONS: Record<string, PlanetInterpretation> = {
  // ═══════════════════════════════════════════
  // СОЕДИНЕНИЯ (Conjunction, 0°)
  // ═══════════════════════════════════════════
  'conjunction-sun-moon': {
    title: 'Соединение Солнце–Луна',
    text: 'Новолуние в натальной карте. Ваша воля и эмоции слиты воедино — вы цельная натура, которая действует из единого внутреннего центра. Вы точно знаете, чего хотите, и ваши чувства не противоречат разуму. Однако вам может быть сложно посмотреть на себя со стороны.',
    strengths: [
      'Цельность личности',
      'Мощная концентрация энергии',
      'Ясность намерений',
    ],
    challenges: [
      'Субъективность',
      'Сложность видеть чужую точку зрения',
      'Зацикленность на себе',
    ],
  },
  'conjunction-venus-mars': {
    title: 'Соединение Венера–Марс',
    text: 'Мощное сочетание женского и мужского начал в одной карте. Вы обладаете сильным магнетизмом и притягательностью. Ваша страсть и нежность переплетены — вы умеете и завоёвывать, и очаровывать. Творческая и сексуальная энергия у вас на высоте.',
    strengths: ['Магнетизм', 'Творческая страсть', 'Умение привлекать'],
    challenges: [
      'Импульсивность в чувствах',
      'Ревность',
      'Сложность разделять любовь и желание',
    ],
  },
  'conjunction-mercury-venus': {
    title: 'Соединение Меркурий–Венера',
    text: 'Ваша речь обаятельна, а мышление эстетично. Вы умеете красиво говорить, писать и выражать свои мысли. Дипломатия — ваш конёк. Вы легко находите общий язык с людьми и создаёте вокруг себя приятную атмосферу.',
    strengths: ['Красноречие', 'Дипломатичность', 'Художественный вкус'],
    challenges: [
      'Поверхностность суждений',
      'Избегание неприятных тем',
      'Лесть',
    ],
  },
  'conjunction-sun-mercury': {
    title: 'Соединение Солнце–Меркурий',
    text: 'Ваш разум и личность тесно связаны. Вы идентифицируете себя через свои идеи и интеллект. Вы красноречивы, уверены в своих мнениях и умеете убеждать. Однако можете быть слишком субъективны в оценках.',
    strengths: [
      'Интеллектуальная уверенность',
      'Убедительность',
      'Ясность мысли',
    ],
    challenges: [
      'Субъективность мышления',
      'Неумение слушать',
      'Интеллектуальная гордыня',
    ],
  },
  'conjunction-moon-venus': {
    title: 'Соединение Луна–Венера',
    text: 'Одно из самых гармоничных соединений. Вы обладаете природным обаянием, мягкостью и эмоциональной теплотой. Вы умеете создавать уют и красоту вокруг себя. Люди тянутся к вам за утешением и нежностью.',
    strengths: [
      'Природное обаяние',
      'Эмоциональная теплота',
      'Эстетический вкус',
    ],
    challenges: [
      'Избегание конфликтов',
      'Потакание себе',
      'Эмоциональная зависимость',
    ],
  },
  'conjunction-mars-jupiter': {
    title: 'Соединение Марс–Юпитер',
    text: 'Мощнейший аспект успеха и экспансии. Ваша энергия огромна, а амбиции — безграничны. Вы действуете масштабно, с размахом и оптимизмом. Удача часто сопутствует вашим начинаниям, особенно в спорте, бизнесе и путешествиях.',
    strengths: ['Колоссальная энергия', 'Оптимизм в действиях', 'Удачливость'],
    challenges: ['Переоценка сил', 'Чрезмерный риск', 'Нетерпеливость'],
  },
  'conjunction-saturn-pluto': {
    title: 'Соединение Сатурн–Плутон',
    text: 'Поколенческий аспект огромной силы. Вы обладаете железной волей и способностью выживать в самых тяжёлых условиях. Вы знаете, что такое настоящая ответственность и власть. Этот аспект даёт глубокую трансформацию через ограничения.',
    strengths: [
      'Несгибаемая воля',
      'Стратегическое мышление',
      'Способность к трансформации',
    ],
    challenges: ['Жёсткость', 'Контроль', 'Склонность к авторитаризму'],
  },

  // ═══════════════════════════════════════════
  // ТРИГОНЫ (Trine, 120°)
  // ═══════════════════════════════════════════
  'trine-sun-moon': {
    title: 'Тригон Солнце–Луна',
    text: 'Гармоничный аспект, дающий внутренний мир и согласие между волей и чувствами. Вы легко понимаете свои потребности и умеете их удовлетворять. Ваши отношения с родителями, как правило, благоприятны, что даёт прочную эмоциональную базу.',
    strengths: [
      'Внутренняя гармония',
      'Эмоциональная стабильность',
      'Естественная уверенность',
    ],
    challenges: [
      'Самодовольство',
      'Лень из-за комфорта',
      'Недостаток мотивации к росту',
    ],
  },
  'trine-venus-jupiter': {
    title: 'Тригон Венера–Юпитер',
    text: 'Один из самых счастливых аспектов в натальной карте. Вы обладаете природным оптимизмом, щедростью и умением наслаждаться жизнью. Удача в любви и финансах — ваш частый спутник. Вы притягиваете изобилие.',
    strengths: ['Щедрость', 'Удачливость в любви', 'Оптимизм'],
    challenges: ['Расточительность', 'Лень', 'Излишний гедонизм'],
  },
  'trine-sun-jupiter': {
    title: 'Тригон Солнце–Юпитер',
    text: 'Аспект врождённой удачи и широты натуры. Вы оптимистичны, великодушны и обладаете природной мудростью. Жизнь часто предоставляет вам возможности для роста. Вы вдохновляете окружающих своей верой в лучшее.',
    strengths: ['Природный оптимизм', 'Великодушие', 'Способность вдохновлять'],
    challenges: ['Самонадеянность', 'Лень', 'Склонность к преувеличениям'],
  },
  'trine-moon-neptune': {
    title: 'Тригон Луна–Нептун',
    text: 'Вы обладаете тончайшей интуицией и богатым воображением. Ваш внутренний мир полон образов, снов и предчувствий. Вы глубоко сострадательны и способны к безусловной любви. Творческие способности — особенно в музыке и поэзии — выражены ярко.',
    strengths: [
      'Мощная интуиция',
      'Творческое воображение',
      'Глубокое сострадание',
    ],
    challenges: ['Иллюзии', 'Размытые границы', 'Впечатлительность'],
  },
  'trine-mars-saturn': {
    title: 'Тригон Марс–Сатурн',
    text: 'Великолепный аспект для достижения целей. Ваша энергия дисциплинирована и направлена. Вы умеете работать долго, упорно и методично. Ваши действия продуманы, а результаты — стабильны и долговечны.',
    strengths: [
      'Дисциплинированная энергия',
      'Выносливость',
      'Стратегическое действие',
    ],
    challenges: [
      'Излишняя серьёзность',
      'Подавление спонтанности',
      'Жёсткость',
    ],
  },

  // ═══════════════════════════════════════════
  // КВАДРАТУРЫ (Square, 90°)
  // ═══════════════════════════════════════════
  'square-sun-moon': {
    title: 'Квадратура Солнце–Луна',
    text: 'Внутреннее напряжение между тем, кто вы есть, и тем, что вы чувствуете. Ваша воля и эмоции часто конфликтуют, создавая ощущение внутреннего разлада. Однако именно это напряжение становится мощным двигателем развития и самопознания.',
    strengths: [
      'Мотивация к росту',
      'Эмоциональная глубина',
      'Способность к самоанализу',
    ],
    challenges: [
      'Внутренний конфликт',
      'Эмоциональная нестабильность',
      'Сложности с родителями',
    ],
  },
  'square-venus-saturn': {
    title: 'Квадратура Венера–Сатурн',
    text: 'Один из самых сложных аспектов для личной жизни. Вы можете ощущать себя недостойным любви или бояться близости. Отношения приходят с задержкой или через трудности. Но именно через эту работу вы обретаете самую зрелую и глубокую любовь.',
    strengths: [
      'Верность',
      'Серьёзное отношение к любви',
      'Эмоциональная зрелость',
    ],
    challenges: ['Страх отвержения', 'Холодность', 'Задержки в личной жизни'],
  },
  'square-mars-saturn': {
    title: 'Квадратура Марс–Сатурн',
    text: 'Аспект «тормоза и газа одновременно». Ваша энергия то блокируется, то прорывается с удвоенной силой. Вы можете испытывать фрустрацию от ограничений, но именно через преодоление вы закаляете характер и достигаете невероятных результатов.',
    strengths: ['Закалённый характер', 'Упорство', 'Способность преодолевать'],
    challenges: [
      'Фрустрация',
      'Подавленная агрессия',
      'Конфликт с авторитетами',
    ],
  },
  'square-sun-pluto': {
    title: 'Квадратура Солнце–Плутон',
    text: 'Мощный аспект трансформации и борьбы за власть. Вы проходите через глубокие кризисы, которые полностью перестраивают вашу личность. Вы обладаете огромной внутренней силой, но можете бороться с потребностью контролировать всё вокруг.',
    strengths: [
      'Трансформационная сила',
      'Психологическая глубина',
      'Регенерация',
    ],
    challenges: ['Борьба за власть', 'Одержимость', 'Кризисы идентичности'],
  },
  'square-moon-pluto': {
    title: 'Квадратура Луна–Плутон',
    text: 'Интенсивный эмоциональный мир. Ваши чувства глубоки, страстны и порой разрушительны. Вы можете переживать эмоциональные кризисы, связанные с матерью, домом или безопасностью. Через проработку этот аспект даёт невероятную психологическую силу.',
    strengths: [
      'Эмоциональная мощь',
      'Психологическая проницательность',
      'Способность к исцелению',
    ],
    challenges: ['Эмоциональные манипуляции', 'Ревность', 'Страх потери'],
  },
  'square-mercury-saturn': {
    title: 'Квадратура Меркурий–Сатурн',
    text: 'Ваше мышление серьёзное, глубокое, но может быть замедленным или пессимистичным. В детстве вас могли критиковать за идеи или речь. Со временем вы развиваете невероятную интеллектуальную дисциплину и становитесь экспертом в своей области.',
    strengths: [
      'Глубина мышления',
      'Интеллектуальная дисциплина',
      'Концентрация',
    ],
    challenges: ['Пессимизм', 'Страх высказываться', 'Ментальные блоки'],
  },

  // ═══════════════════════════════════════════
  // ОППОЗИЦИИ (Opposition, 180°)
  // ═══════════════════════════════════════════
  'opposition-sun-moon': {
    title: 'Оппозиция Солнце–Луна',
    text: 'Полнолуние в натальной карте. Ваша воля и эмоции находятся в постоянном диалоге — иногда в конфликте, иногда в балансе. Вы хорошо понимаете полярности жизни и можете быть отличным посредником. Отношения играют ключевую роль в вашем развитии.',
    strengths: [
      'Объективность',
      'Понимание других',
      'Способность к компромиссу',
    ],
    challenges: ['Внутренние метания', 'Зависимость от отношений', 'Колебания'],
  },
  'opposition-venus-pluto': {
    title: 'Оппозиция Венера–Плутон',
    text: 'Любовь для вас — это всегда трансформация. Ваши отношения интенсивны, страстны и порой болезненны. Вы притягиваете мощные, судьбоносные связи, которые полностью меняют вашу жизнь. Тема власти и контроля в любви — ваш главный урок.',
    strengths: ['Глубина чувств', 'Магнетизм', 'Способность к полной отдаче'],
    challenges: [
      'Ревность',
      'Манипуляции в любви',
      'Болезненные привязанности',
    ],
  },
  'opposition-mars-pluto': {
    title: 'Оппозиция Марс–Плутон',
    text: 'Аспект колоссальной энергии и борьбы за власть. Вы обладаете невероятной силой воли и способностью действовать в экстремальных условиях. Однако вы можете притягивать конфликты и борьбу за доминирование.',
    strengths: [
      'Невероятная сила воли',
      'Бесстрашие',
      'Способность к выживанию',
    ],
    challenges: [
      'Борьба за власть',
      'Агрессия',
      'Притяжение экстремальных ситуаций',
    ],
  },
  'opposition-jupiter-saturn': {
    title: 'Оппозиция Юпитер–Сатурн',
    text: 'Вечный диалог между расширением и ограничением, оптимизмом и реализмом. Вы колеблетесь между желанием рисковать и потребностью в безопасности. Когда вы находите баланс, то способны строить масштабные и устойчивые проекты.',
    strengths: [
      'Мудрый баланс',
      'Реалистичный оптимизм',
      'Стратегическое видение',
    ],
    challenges: [
      'Колебания',
      'Страх неудачи',
      'Конфликт амбиций и осторожности',
    ],
  },

  // ═══════════════════════════════════════════
  // СЕКСТИЛИ (Sextile, 60°)
  // ═══════════════════════════════════════════
  'sextile-sun-mars': {
    title: 'Секстиль Солнце–Марс',
    text: 'Гармоничная связь вашей личности и энергии действия. Вы умеете эффективно направлять свою волю в конкретные дела. Вы активны, но не агрессивны; решительны, но не импульсивны. Спорт и физическая активность даются вам легко.',
    strengths: [
      'Продуктивная энергия',
      'Решительность без агрессии',
      'Физическая активность',
    ],
    challenges: [
      'Недоиспользование потенциала',
      'Привычка к лёгким путям',
      'Самоуспокоенность',
    ],
  },
  'sextile-moon-mercury': {
    title: 'Секстиль Луна–Меркурий',
    text: 'Ваш разум и чувства работают в тандеме. Вы умеете выражать свои эмоции словами и понимать чувства других через общение. Вы прекрасный слушатель и собеседник, обладающий эмоциональным интеллектом.',
    strengths: [
      'Эмоциональный интеллект',
      'Умение слушать',
      'Выразительная речь',
    ],
    challenges: [
      'Рационализация чувств',
      'Болтливость',
      'Поверхностная эмпатия',
    ],
  },
  'sextile-venus-mars': {
    title: 'Секстиль Венера–Марс',
    text: 'Приятный баланс мужской и женской энергий. Вы обаятельны, привлекательны и умеете флиртовать с лёгкостью. В отношениях вы сочетаете нежность и страсть. Творческая энергия выражена гармонично.',
    strengths: [
      'Обаяние',
      'Гармония в отношениях',
      'Творческая продуктивность',
    ],
    challenges: [
      'Поверхностность связей',
      'Лень в отношениях',
      'Избегание глубины',
    ],
  },
  'sextile-jupiter-uranus': {
    title: 'Секстиль Юпитер–Уран',
    text: 'Аспект удачных озарений и неожиданных возможностей. Вы открыты новому, легко принимаете перемены и часто оказываетесь в нужном месте в нужное время. Инновации и прогрессивные идеи — ваша стихия.',
    strengths: [
      'Удачные озарения',
      'Открытость новому',
      'Прогрессивное мышление',
    ],
    challenges: [
      'Непоследовательность',
      'Разбросанность',
      'Нетерпение к рутине',
    ],
  },
  'sextile-mercury-jupiter': {
    title: 'Секстиль Меркурий–Юпитер',
    text: 'Ваш ум широк, оптимистичен и способен видеть большую картину. Вы легко учитесь, любите путешествовать и обмениваться идеями. Вы прекрасный рассказчик и учитель, способный вдохновлять через слово.',
    strengths: [
      'Широта мышления',
      'Способность к обучению',
      'Оптимистичный ум',
    ],
    challenges: [
      'Невнимание к деталям',
      'Многословие',
      'Поверхностность знаний',
    ],
  },
  'sextile-moon-saturn': {
    title: 'Секстиль Луна–Сатурн',
    text: 'Ваши эмоции зрелы и стабильны. Вы умеете управлять своими чувствами и не поддаваться панике. Вы надёжный эмоциональный партнёр, на которого можно положиться. Ваша мудрость приходит через жизненный опыт.',
    strengths: [
      'Эмоциональная зрелость',
      'Надёжность',
      'Практическая мудрость',
    ],
    challenges: [
      'Сдержанность чувств',
      'Излишняя серьёзность',
      'Страх уязвимости',
    ],
  },
}

// ═══════════════════════════════════════════
// ПЛАНЕТЫ В ДОМАХ
// ═══════════════════════════════════════════

const HOUSE_THEMES = [
  {
    name: '1 доме',
    sphere: 'личности, внешности и самовыражения',
    area: 'Первый дом — это вы сами, ваша внешность, манера поведения и первое впечатление, которое вы производите.',
  },
  {
    name: '2 доме',
    sphere: 'финансов, ценностей и материальных ресурсов',
    area: 'Второй дом отвечает за деньги, имущество, самооценку и то, что вы считаете по-настоящему ценным.',
  },
  {
    name: '3 доме',
    sphere: 'общения, обучения и ближайшего окружения',
    area: 'Третий дом связан с повседневным общением, братьями и сёстрами, короткими поездками и начальным образованием.',
  },
  {
    name: '4 доме',
    sphere: 'дома, семьи и эмоциональных корней',
    area: 'Четвёртый дом — это ваш дом, семья, родители, детские воспоминания и ощущение внутренней безопасности.',
  },
  {
    name: '5 доме',
    sphere: 'творчества, романтики и самовыражения',
    area: 'Пятый дом отвечает за творчество, романтические увлечения, детей, хобби и всё, что приносит радость.',
  },
  {
    name: '6 доме',
    sphere: 'работы, здоровья и повседневной рутины',
    area: 'Шестой дом связан с ежедневным трудом, здоровьем, привычками, служением и отношениями с коллегами.',
  },
  {
    name: '7 доме',
    sphere: 'партнёрства, брака и открытых отношений',
    area: 'Седьмой дом отвечает за брак, деловое партнёрство, открытых врагов и то, как вы взаимодействуете один на один.',
  },
  {
    name: '8 доме',
    sphere: 'трансформации, кризисов и глубинных процессов',
    area: 'Восьмой дом связан с кризисами, наследством, чужими деньгами, сексуальностью и психологической трансформацией.',
  },
  {
    name: '9 доме',
    sphere: 'философии, путешествий и высшего знания',
    area: 'Девятый дом отвечает за дальние путешествия, высшее образование, религию, философию и расширение мировоззрения.',
  },
  {
    name: '10 доме',
    sphere: 'карьеры, статуса и общественного признания',
    area: 'Десятый дом — это ваша карьера, репутация, амбиции, отношения с начальством и место в обществе.',
  },
  {
    name: '11 доме',
    sphere: 'дружбы, коллективов и надежд на будущее',
    area: 'Одиннадцатый дом связан с друзьями, единомышленниками, коллективными проектами и вашими мечтами о будущем.',
  },
  {
    name: '12 доме',
    sphere: 'подсознания, уединения и духовности',
    area: 'Двенадцатый дом — это тайны, подсознание, уединение, скрытые враги, кармические долги и духовные практики.',
  },
]

interface HousePlanetConfig {
  key: string
  name: string
  energy: string
  houseEffects: { strengths: string[]; challenges: string[]; flavor: string }[]
}

const HOUSE_PLANETS: HousePlanetConfig[] = [
  {
    key: 'sun',
    name: 'Солнце',
    energy: 'воля, жизненная сила и стремление к самореализации',
    houseEffects: [
      {
        strengths: [
          'Яркая индивидуальность',
          'Лидерские качества',
          'Сильная воля',
        ],
        challenges: ['Эгоцентризм', 'Потребность во внимании', 'Доминирование'],
        flavor:
          'Вы сияете через свою личность. Ваша задача — быть собой, проявлять инициативу и вести за собой. Окружающие видят в вас яркого, уверенного человека.',
      },
      {
        strengths: ['Умение зарабатывать', 'Щедрость', 'Сильная самооценка'],
        challenges: [
          'Расточительность',
          'Зависимость от материального',
          'Гордыня через деньги',
        ],
        flavor:
          'Ваша самореализация тесно связана с финансами. Вы умеете зарабатывать и тратить с размахом. Деньги для вас — способ подтвердить свою ценность.',
      },
      {
        strengths: ['Красноречие', 'Любознательность', 'Талант к обучению'],
        challenges: ['Поверхностность', 'Рассеянность', 'Болтливость'],
        flavor:
          'Вы реализуетесь через общение, обучение и обмен информацией. Ваш ум активен, а слово — ваш главный инструмент влияния.',
      },
      {
        strengths: [
          'Семейные ценности',
          'Эмоциональная глубина',
          'Создание уюта',
        ],
        challenges: ['Зависимость от семьи', 'Ностальгия', 'Закрытость'],
        flavor:
          'Ваш дом и семья — центр вашей жизни. Вы реализуетесь через создание прочного фундамента и заботу о близких.',
      },
      {
        strengths: ['Творческий потенциал', 'Романтичность', 'Любовь к детям'],
        challenges: [
          'Драматизация',
          'Зависимость от похвалы',
          'Инфантильность',
        ],
        flavor:
          'Вы сияете через творчество, романтику и самовыражение. Ваша жизнь — это сцена, а вы — главный герой.',
      },
      {
        strengths: ['Трудолюбие', 'Забота о здоровье', 'Служение'],
        challenges: ['Трудоголизм', 'Критичность', 'Подчинение рутине'],
        flavor:
          'Вы реализуетесь через работу и служение. Ваш труд — это способ проявить себя и принести пользу миру.',
      },
      {
        strengths: ['Дипломатичность', 'Партнёрские качества', 'Обаяние'],
        challenges: [
          'Зависимость от партнёра',
          'Потеря себя в отношениях',
          'Конфликтность',
        ],
        flavor:
          'Вы раскрываетесь через партнёрство. Отношения — зеркало, в котором вы познаёте себя.',
      },
      {
        strengths: [
          'Психологическая глубина',
          'Способность к трансформации',
          'Магнетизм',
        ],
        challenges: ['Кризисы', 'Борьба за власть', 'Скрытность'],
        flavor:
          'Вы проходите через глубокие трансформации. Кризисы — ваш путь к перерождению и обретению истинной силы.',
      },
      {
        strengths: ['Мудрость', 'Любовь к путешествиям', 'Философский ум'],
        challenges: ['Догматизм', 'Бегство от реальности', 'Морализаторство'],
        flavor:
          'Вы реализуетесь через познание мира, путешествия и поиск высшего смысла. Ваша миссия — расширять горизонты.',
      },
      {
        strengths: ['Амбициозность', 'Лидерство в карьере', 'Авторитет'],
        challenges: ['Карьеризм', 'Холодность', 'Давление ответственности'],
        flavor:
          'Карьера и общественное признание — ваш путь самореализации. Вы стремитесь к вершинам и готовы нести ответственность.',
      },
      {
        strengths: ['Дружелюбие', 'Командный дух', 'Прогрессивность'],
        challenges: ['Отстранённость', 'Зависимость от группы', 'Утопизм'],
        flavor:
          'Вы реализуетесь через друзей, коллективы и общие идеалы. Ваша сила — в единомышленниках и общих целях.',
      },
      {
        strengths: ['Интуиция', 'Духовность', 'Сострадание'],
        challenges: ['Скрытность', 'Жертвенность', 'Потеря себя'],
        flavor:
          'Ваша самореализация происходит в уединении и через духовные практики. Вы обладаете глубокой связью с подсознанием.',
      },
    ],
  },
  {
    key: 'moon',
    name: 'Луна',
    energy: 'эмоции, интуиция и потребность в безопасности',
    houseEffects: [
      {
        strengths: ['Эмоциональная открытость', 'Интуиция', 'Адаптивность'],
        challenges: [
          'Переменчивость',
          'Зависимость от настроения',
          'Ранимость',
        ],
        flavor:
          'Ваши эмоции на виду. Вы чутко реагируете на окружение и легко адаптируетесь, но настроение может меняться стремительно.',
      },
      {
        strengths: [
          'Финансовая интуиция',
          'Умение копить',
          'Ценность комфорта',
        ],
        challenges: [
          'Эмоциональные траты',
          'Нестабильность доходов',
          'Привязанность к вещам',
        ],
        flavor:
          'Ваше эмоциональное состояние тесно связано с финансами. Деньги дают вам ощущение безопасности.',
      },
      {
        strengths: ['Эмпатичное общение', 'Хорошая память', 'Связь с родными'],
        challenges: ['Сплетни', 'Нервозность', 'Эмоциональная болтливость'],
        flavor:
          'Вы перерабатываете эмоции через общение. Разговоры, переписки и обмен новостями — ваш способ обрести покой.',
      },
      {
        strengths: [
          'Глубокая привязанность к дому',
          'Забота о семье',
          'Интуиция',
        ],
        challenges: ['Зависимость от прошлого', 'Гиперопека', 'Обидчивость'],
        flavor:
          'Дом — ваше святилище. Вы нуждаетесь в уютном, безопасном пространстве и глубоко привязаны к семье и корням.',
      },
      {
        strengths: [
          'Творческая чувствительность',
          'Любовь к детям',
          'Романтичность',
        ],
        challenges: [
          'Драматизация чувств',
          'Капризность',
          'Эмоциональная зависимость в любви',
        ],
        flavor:
          'Ваши эмоции ярко проявляются в творчестве и романтике. Вы нуждаетесь в любви и восхищении для душевного комфорта.',
      },
      {
        strengths: [
          'Заботливость на работе',
          'Интуитивное понимание здоровья',
          'Служение',
        ],
        challenges: [
          'Тревожность о здоровье',
          'Эмоциональное выгорание',
          'Зависимость от рутины',
        ],
        flavor:
          'Вы находите эмоциональный комфорт в работе и заботе о других. Рутина и порядок успокаивают вас.',
      },
      {
        strengths: [
          'Эмпатия в отношениях',
          'Заботливый партнёр',
          'Интуиция о людях',
        ],
        challenges: [
          'Эмоциональная зависимость',
          'Проекции на партнёра',
          'Переменчивость в отношениях',
        ],
        flavor:
          'Вы нуждаетесь в эмоционально близком партнёре. Отношения — ваш главный источник душевного комфорта.',
      },
      {
        strengths: [
          'Психологическая глубина',
          'Мощная интуиция',
          'Способность к исцелению',
        ],
        challenges: ['Эмоциональные кризисы', 'Ревность', 'Страх потери'],
        flavor:
          'Ваши эмоции глубоки и интенсивны. Вы проходите через эмоциональные трансформации, которые делают вас сильнее.',
      },
      {
        strengths: [
          'Тяга к путешествиям',
          'Философский ум',
          'Эмоциональная широта',
        ],
        challenges: [
          'Беспокойство',
          'Бегство от чувств',
          'Идеализация далёкого',
        ],
        flavor:
          'Вы находите душевный покой в путешествиях, учёбе и духовных поисках. Вам нужен простор для эмоций.',
      },
      {
        strengths: ['Публичная эмпатия', 'Популярность', 'Забота о репутации'],
        challenges: [
          'Зависимость от общественного мнения',
          'Эмоциональное давление карьеры',
          'Публичная уязвимость',
        ],
        flavor:
          'Ваши эмоции видны публике. Вы можете быть очень популярны, но и уязвимы перед общественным мнением.',
      },
      {
        strengths: [
          'Дружелюбие',
          'Эмоциональная поддержка друзей',
          'Коллективная интуиция',
        ],
        challenges: [
          'Зависимость от друзей',
          'Переменчивость в дружбе',
          'Размытые границы в группе',
        ],
        flavor:
          'Друзья — ваша семья. Вы нуждаетесь в принадлежности к группе и находите утешение в коллективе.',
      },
      {
        strengths: [
          'Мощная интуиция',
          'Духовная чувствительность',
          'Сострадание',
        ],
        challenges: ['Тревожность', 'Скрытые страхи', 'Эмоциональная изоляция'],
        flavor:
          'Ваш внутренний мир богат и загадочен. Вы обладаете мощной интуицией, но можете страдать от скрытых тревог.',
      },
    ],
  },
  {
    key: 'mercury',
    name: 'Меркурий',
    energy: 'мышление, общение и обработка информации',
    houseEffects: [
      {
        strengths: ['Быстрый ум', 'Красноречие', 'Любознательность'],
        challenges: ['Нервозность', 'Болтливость', 'Поверхностность'],
        flavor:
          'Ваш ум — ваша визитная карточка. Вы производите впечатление умного, общительного человека с быстрой реакцией.',
      },
      {
        strengths: [
          'Коммерческая смекалка',
          'Умение торговать',
          'Практический ум',
        ],
        challenges: ['Хитрость', 'Зацикленность на деньгах', 'Расчётливость'],
        flavor:
          'Ваш интеллект направлен на заработок. Вы умеете считать, торговать и находить выгодные возможности.',
      },
      {
        strengths: [
          'Блестящий коммуникатор',
          'Талант к языкам',
          'Любовь к учёбе',
        ],
        challenges: ['Информационная перегрузка', 'Сплетни', 'Рассеянность'],
        flavor:
          'Это сильное положение. Ваш ум подвижен, вы обожаете учиться, общаться и обмениваться идеями.',
      },
      {
        strengths: [
          'Хорошая память',
          'Семейный историк',
          'Интуитивное мышление',
        ],
        challenges: [
          'Субъективность',
          'Зацикленность на прошлом',
          'Обидчивость в спорах',
        ],
        flavor:
          'Ваше мышление связано с семьёй и домом. Вы хорошо помните детство и любите семейные истории.',
      },
      {
        strengths: ['Творческое мышление', 'Артистизм', 'Игривый ум'],
        challenges: [
          'Драматизация',
          'Интеллектуальная гордыня',
          'Несерьёзность',
        ],
        flavor:
          'Ваш ум творческий и игривый. Вы умеете увлечь аудиторию и выражать идеи ярко и артистично.',
      },
      {
        strengths: [
          'Аналитический ум',
          'Внимание к деталям',
          'Системное мышление',
        ],
        challenges: ['Занудство', 'Тревожность', 'Критичность'],
        flavor:
          'Ваш интеллект направлен на анализ, систематизацию и решение практических задач. Вы мастер деталей.',
      },
      {
        strengths: [
          'Дипломатичность',
          'Умение договариваться',
          'Объективность',
        ],
        challenges: [
          'Нерешительность',
          'Зависимость от чужого мнения',
          'Споры',
        ],
        flavor:
          'Ваше мышление ориентировано на партнёрство. Вы умеете видеть обе стороны и находить компромиссы.',
      },
      {
        strengths: [
          'Проницательность',
          'Исследовательский ум',
          'Психологическое чутьё',
        ],
        challenges: ['Подозрительность', 'Скрытность', 'Навязчивые мысли'],
        flavor:
          'Ваш ум проникает в глубину вещей. Вы прирождённый исследователь, психолог и детектив.',
      },
      {
        strengths: [
          'Философский ум',
          'Любовь к путешествиям',
          'Широта мышления',
        ],
        challenges: ['Невнимание к деталям', 'Догматизм', 'Многословие'],
        flavor:
          'Ваш интеллект масштабен и философичен. Вы мыслите глобально и любите обсуждать большие идеи.',
      },
      {
        strengths: [
          'Стратегическое мышление',
          'Ораторское мастерство',
          'Карьерный интеллект',
        ],
        challenges: [
          'Карьеризм в мышлении',
          'Холодная логика',
          'Расчётливость',
        ],
        flavor:
          'Ваш ум направлен на карьеру и достижения. Вы умеете планировать и выстраивать стратегии успеха.',
      },
      {
        strengths: [
          'Прогрессивное мышление',
          'Командная коммуникация',
          'Изобретательность',
        ],
        challenges: ['Отстранённость', 'Упрямство в идеях', 'Разбросанность'],
        flavor:
          'Ваш ум оригинален и устремлён в будущее. Вы генерируете идеи в коллективе и вдохновляете единомышленников.',
      },
      {
        strengths: ['Интуитивное мышление', 'Поэтичность', 'Медитативный ум'],
        challenges: ['Рассеянность', 'Иллюзии', 'Скрытность мыслей'],
        flavor:
          'Ваше мышление интуитивно и образно. Вы обрабатываете информацию через подсознание, сны и медитации.',
      },
    ],
  },
  {
    key: 'venus',
    name: 'Венера',
    energy: 'любовь, красота и ценности',
    houseEffects: [
      {
        strengths: ['Обаяние', 'Привлекательная внешность', 'Миролюбие'],
        challenges: ['Нарциссизм', 'Лень', 'Зависимость от внешности'],
        flavor:
          'Вы обладаете природным обаянием и привлекательностью. Люди тянутся к вам за красотой и гармонией.',
      },
      {
        strengths: ['Финансовая удача', 'Хороший вкус', 'Умение наслаждаться'],
        challenges: ['Расточительность', 'Материализм', 'Лень'],
        flavor:
          'Вы умеете зарабатывать и красиво тратить. Деньги приходят к вам через обаяние, искусство или красоту.',
      },
      {
        strengths: ['Обаятельная речь', 'Лёгкость общения', 'Дипломатичность'],
        challenges: ['Поверхностность', 'Флирт', 'Непостоянство'],
        flavor:
          'Ваше общение наполнено обаянием и лёгкостью. Вы умеете очаровывать словами и создавать приятную атмосферу.',
      },
      {
        strengths: ['Любовь к дому', 'Создание уюта', 'Семейная гармония'],
        challenges: ['Зависимость от семьи', 'Ностальгия', 'Собственничество'],
        flavor:
          'Ваш дом — произведение искусства. Вы создаёте красоту и гармонию в семейном пространстве.',
      },
      {
        strengths: ['Творческий талант', 'Романтичность', 'Любовь к детям'],
        challenges: [
          'Драматизация любви',
          'Зависимость от романтики',
          'Инфантильность',
        ],
        flavor:
          'Любовь и творчество — центр вашей жизни. Вы романтик, артист и ценитель прекрасного.',
      },
      {
        strengths: [
          'Гармония на работе',
          'Эстетика в деталях',
          'Забота о коллегах',
        ],
        challenges: [
          'Служебные романы',
          'Лень на работе',
          'Перфекционизм в мелочах',
        ],
        flavor:
          'Вы привносите красоту и гармонию в рабочую среду. Ваши отношения с коллегами, как правило, тёплые.',
      },
      {
        strengths: [
          'Гармоничные отношения',
          'Дипломатичность',
          'Привлекательный партнёр',
        ],
        challenges: [
          'Зависимость от партнёра',
          'Идеализация',
          'Страх одиночества',
        ],
        flavor:
          'Это сильное положение для любви. Вы созданы для партнёрства и притягиваете красивых, обаятельных людей.',
      },
      {
        strengths: [
          'Магнетизм',
          'Глубокая страсть',
          'Финансовая выгода через партнёра',
        ],
        challenges: [
          'Ревность',
          'Манипуляции в любви',
          'Болезненные привязанности',
        ],
        flavor:
          'Ваша любовь глубока и трансформирующа. Вы притягиваете интенсивные, судьбоносные отношения.',
      },
      {
        strengths: [
          'Любовь к путешествиям',
          'Межкультурные связи',
          'Философия любви',
        ],
        challenges: [
          'Идеализация далёкого',
          'Непостоянство',
          'Бегство от близости',
        ],
        flavor:
          'Вы находите любовь в путешествиях, учёбе и духовных поисках. Вас привлекают иностранцы и другие культуры.',
      },
      {
        strengths: [
          'Публичное обаяние',
          'Карьера в искусстве',
          'Хорошая репутация',
        ],
        challenges: [
          'Показная любовь',
          'Карьера через связи',
          'Зависимость от статуса',
        ],
        flavor:
          'Ваше обаяние работает на карьеру. Вы можете добиться успеха в искусстве, моде или дипломатии.',
      },
      {
        strengths: [
          'Дружелюбие',
          'Социальная популярность',
          'Гармония в группе',
        ],
        challenges: [
          'Поверхностные связи',
          'Зависимость от друзей',
          'Непостоянство',
        ],
        flavor:
          'Вы находите любовь и радость через друзей и коллективы. Ваш круг общения широк и разнообразен.',
      },
      {
        strengths: ['Безусловная любовь', 'Духовная красота', 'Сострадание'],
        challenges: ['Тайные романы', 'Жертвенность в любви', 'Иллюзии'],
        flavor:
          'Ваша любовь тиха, глубока и часто скрыта. Вы способны на безусловное принятие и духовную близость.',
      },
    ],
  },
  {
    key: 'mars',
    name: 'Марс',
    energy: 'действие, энергия и воля к победе',
    houseEffects: [
      {
        strengths: ['Решительность', 'Физическая сила', 'Лидерство'],
        challenges: ['Агрессивность', 'Импульсивность', 'Конфликтность'],
        flavor:
          'Вы действуете быстро и решительно. Ваша энергия направлена на самоутверждение и завоевание мира.',
      },
      {
        strengths: [
          'Умение зарабатывать',
          'Финансовая активность',
          'Предприимчивость',
        ],
        challenges: ['Импульсивные траты', 'Финансовые конфликты', 'Жадность'],
        flavor:
          'Ваша энергия направлена на заработок. Вы активно добиваетесь финансовой независимости.',
      },
      {
        strengths: ['Острый ум', 'Прямолинейность', 'Активное общение'],
        challenges: ['Споры', 'Резкость в словах', 'Конфликты с соседями'],
        flavor:
          'Ваша речь энергична и прямолинейна. Вы не боитесь спорить и отстаивать свою точку зрения.',
      },
      {
        strengths: ['Защита семьи', 'Активность дома', 'Ремонт и обустройство'],
        challenges: [
          'Конфликты в семье',
          'Домашнее беспокойство',
          'Авторитарность дома',
        ],
        flavor:
          'Ваша энергия направлена на дом и семью. Вы активно обустраиваете пространство и защищаете близких.',
      },
      {
        strengths: [
          'Творческая энергия',
          'Спортивность',
          'Страстная романтика',
        ],
        challenges: [
          'Драматизация',
          'Конкуренция в любви',
          'Импульсивные романы',
        ],
        flavor:
          'Ваша энергия бьёт ключом в творчестве и романтике. Вы страстный любовник и азартный игрок.',
      },
      {
        strengths: ['Трудолюбие', 'Эффективность', 'Физическая выносливость'],
        challenges: [
          'Трудоголизм',
          'Конфликты на работе',
          'Проблемы со здоровьем от перенапряжения',
        ],
        flavor:
          'Вы работаете с полной отдачей. Ваша энергия направлена на достижение результатов в ежедневном труде.',
      },
      {
        strengths: [
          'Страстные отношения',
          'Активный партнёр',
          'Умение бороться за любовь',
        ],
        challenges: [
          'Конфликты в браке',
          'Конкуренция с партнёром',
          'Притяжение агрессивных людей',
        ],
        flavor:
          'Ваши отношения полны страсти и динамики. Вам нужен партнёр, с которым можно вместе действовать.',
      },
      {
        strengths: [
          'Бесстрашие',
          'Способность к выживанию',
          'Сексуальная энергия',
        ],
        challenges: ['Кризисы', 'Борьба за власть', 'Разрушительные тенденции'],
        flavor:
          'Ваша энергия направлена на трансформацию. Вы не боитесь кризисов и умеете возрождаться из пепла.',
      },
      {
        strengths: ['Любовь к приключениям', 'Борьба за идеалы', 'Энтузиазм'],
        challenges: [
          'Фанатизм',
          'Бестактность',
          'Конфликты на почве убеждений',
        ],
        flavor:
          'Вы борец за идеалы и искатель приключений. Ваша энергия направлена на расширение горизонтов.',
      },
      {
        strengths: [
          'Карьерная амбициозность',
          'Лидерство в профессии',
          'Целеустремлённость',
        ],
        challenges: ['Конфликты с начальством', 'Карьеризм', 'Авторитарность'],
        flavor:
          'Ваша энергия направлена на карьеру. Вы амбициозны, решительны и готовы бороться за место под солнцем.',
      },
      {
        strengths: [
          'Активность в коллективе',
          'Борьба за общие цели',
          'Лидерство в группе',
        ],
        challenges: ['Конфликты с друзьями', 'Бунтарство', 'Нетерпимость'],
        flavor:
          'Вы активный участник коллективных проектов. Ваша энергия направлена на достижение общих целей.',
      },
      {
        strengths: ['Скрытая сила', 'Интуитивные действия', 'Духовная воля'],
        challenges: ['Скрытая агрессия', 'Саморазрушение', 'Тайные враги'],
        flavor:
          'Ваша энергия работает из подсознания. Вы действуете интуитивно и часто скрываете свои истинные мотивы.',
      },
    ],
  },
]

export const HOUSES_INTERPRETATIONS: Record<string, PlanetInterpretation> = {}

HOUSE_PLANETS.forEach((planet) => {
  planet.houseEffects.forEach((effect, houseIdx) => {
    const house = HOUSE_THEMES[houseIdx]
    HOUSES_INTERPRETATIONS[`${planet.key}-house-${houseIdx + 1}`] = {
      title: `${planet.name} в ${house.name}`,
      text: `${house.area} Когда ${planet.name} — планета, символизирующая ${planet.energy} — попадает в эту сферу, ${effect.flavor}`,
      strengths: effect.strengths,
      challenges: effect.challenges,
    }
  })
})
